import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import QuizImageUpload from "./QuizImageUpload"; // ← your existing image uploader

const SubjectiveQueandAnsModal = ({
  show,
  handleClose,
  effectiveCourseId,
  activeSection,
  onAdded,
  editMode = false,
  existingQuestion = null,
}) => {
  const [question, setQuestion] = useState("");
  const [modelAnswer, setModelAnswer] = useState("");
  const [explanation, setExplanation] = useState("");
  const [totalMarks, setTotalMarks] = useState("5");
  const [isQuestionImage, setIsQuestionImage] = useState(false);
  const [questionImages, setQuestionImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const imageUploadRef = useRef(null);

  const canUploadImages = effectiveCourseId && activeSection?.sectionId;

  // Fill form when editing
  useEffect(() => {
    if (editMode && existingQuestion && show) {
      console.log("SubjectiveQueandAnsModal - Loading question for edit:", existingQuestion);
      console.log("SubjectiveQueandAnsModal - All question properties:", Object.keys(existingQuestion));
      console.log("SubjectiveQueandAnsModal - Full question object:", JSON.stringify(existingQuestion, null, 2));
      
      // Handle question text
      setQuestion(existingQuestion.question || "");
      
      // Handle model answer - check multiple possible property names
      // IMPORTANT: Check all possible variations
      const modelAnswerValue = existingQuestion.modelAnswer 
        || existingQuestion.model_answer 
        || existingQuestion.modelAnswerText
        || existingQuestion.answer 
        || existingQuestion.correctAnswer
        || existingQuestion.expectedAnswer
        || "";
      
      setModelAnswer(modelAnswerValue);
      setExplanation(existingQuestion.explanation || "");
      
      // Handle total marks - convert number to string if needed
      // IMPORTANT: Check all possible variations
      let marksValue = existingQuestion.totalMarks 
        || existingQuestion.marks 
        || existingQuestion.total_marks
        || existingQuestion.maxMarks
        || existingQuestion.points;
      
      if (marksValue !== undefined && marksValue !== null && marksValue !== "") {
        // Convert to string for the input field
        setTotalMarks(String(marksValue));
      } else {
        // If no marks found, use default but log it
        console.warn("SubjectiveQueandAnsModal - No totalMarks found, using default 10");
        setTotalMarks("10"); // Default fallback
      }

      console.log("SubjectiveQueandAnsModal - Populated values:", {
        question: existingQuestion.question,
        modelAnswer: modelAnswerValue,
        totalMarks: marksValue,
        finalTotalMarks: marksValue !== undefined && marksValue !== null ? String(marksValue) : "10",
        allProperties: Object.keys(existingQuestion),
        questionValues: {
          modelAnswer: existingQuestion.modelAnswer,
          model_answer: existingQuestion.model_answer,
          answer: existingQuestion.answer,
          totalMarks: existingQuestion.totalMarks,
          marks: existingQuestion.marks
        }
      });

      const hasImages = existingQuestion.imageQuestion?.length > 0;
      setIsQuestionImage(hasImages);
      setQuestionImages(
        hasImages
          ? existingQuestion.imageQuestion.map((url) => ({ url, description: "" }))
          : []
      );
      
      // Clear any previous errors when loading
      setErrors({});
    } else if (show) {
      // Fresh add
      setQuestion("");
      setModelAnswer("");
      setExplanation("");
      setTotalMarks("10");
      setIsQuestionImage(false);
      setQuestionImages([]);
      setErrors({});
    }
  }, [editMode, existingQuestion, show]);

  const handleImageChange = (images) => {
    setQuestionImages(images);
    setErrors((prev) => ({ ...prev, images: "" }));
  };

  const handleSave = async () => {
    const newErrors = {};
    if (!question.trim()) newErrors.question = "Question is required";
    if (!modelAnswer.trim()) newErrors.answer = "Model answer is required";
    
    // Convert totalMarks to number for validation
    const marksNum = Number(totalMarks);
    if (!totalMarks || isNaN(marksNum) || marksNum <= 0) {
      newErrors.marks = "Enter valid marks";
    }

    if (isQuestionImage && questionImages.length === 0) {
      newErrors.images = "Please upload at least one image";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      let finalImages = questionImages;

      // Upload images if needed
      if (isQuestionImage && imageUploadRef.current) {
        const uploaded = await imageUploadRef.current.uploadImages();
        if (uploaded && uploaded.length > 0) {
          finalImages = uploaded;
        }
      }

      // Delete removed images in edit mode
      if (editMode && isQuestionImage && imageUploadRef.current) {
        await imageUploadRef.current.processDeletions();
      }

      const questionData = {
        id: editMode ? existingQuestion.id : Date.now(),
        type: "subjective",
        question: question.trim(),
        modelAnswer: modelAnswer.trim(),
        explanation: explanation.trim(),
        totalMarks: Number(totalMarks),
        imageQuestion: isQuestionImage
          ? finalImages.map((img) =>
            typeof img === "string" ? img : img.url.split("/").pop()
          )
          : [],
        // Preserve any other properties from the original question
        ...(editMode && existingQuestion ? {
          _id: existingQuestion._id,
          questionId: existingQuestion.questionId,
          // Preserve any other properties that might exist
          ...Object.fromEntries(
            Object.entries(existingQuestion).filter(([key]) => 
              !['question', 'modelAnswer', 'model_answer', 'answer', 'totalMarks', 'marks', 'total_marks', 'imageQuestion', 'type'].includes(key)
            )
          )
        } : {})
      };

      console.log("SubjectiveQueandAnsModal - Saving question data:", questionData);
      onAdded(questionData);
    } catch (err) {
      console.error("Save failed:", err);
      alert("Failed to save question");
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setQuestion("");
    setModelAnswer("");
    setExplanation("");
    setTotalMarks("10");
    setIsQuestionImage(false);
    setQuestionImages([]);
    setErrors({});
    handleClose();
  };

  return (
    <Modal show={show} onHide={closeModal} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {editMode ? "Edit Subjective Question" : "Add Subjective Question"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          {/* Question */}
          <Form.Group className="mb-3">
            <Form.Label>Question <span className="text-danger">*</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Write your question here..."
              isInvalid={!!errors.question}
            />
            {errors.question && (
              <div className="text-danger small mt-1">{errors.question}</div>
            )}
          </Form.Group>

          {/* Image Toggle */}
          {/* <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Add images to question"
              checked={isQuestionImage}
              onChange={(e) => setIsQuestionImage(e.target.checked)}
              disabled={!canUploadImages}
            />
            {!canUploadImages && (
              <small className="text-danger">
                Course & Section required for image upload
              </small>
            )}
          </Form.Group> */}

          {/* Image Upload */}
          {/* {isQuestionImage && canUploadImages && (
            <Form.Group className="mb-4">
              <Form.Label>Question Images</Form.Label>
              <QuizImageUpload
                ref={imageUploadRef}
                onImageUpload={handleImageChange}
                existingImages={questionImages}
                effectiveCourseId={effectiveCourseId}
                activeSection={activeSection}
                editQuiz={editMode}
              />
              {errors.images && (
                <div className="text-danger small">{errors.images}</div>
              )}
            </Form.Group>
          )} */}

          {/* Model Answer */}
          <Form.Group className="mb-3">
            <Form.Label>Model Answer (for grading) <span className="text-danger">*</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              value={modelAnswer}
              onChange={(e) => setModelAnswer(e.target.value)}
              placeholder="Write the expected answer / marking guide..."
              isInvalid={!!errors.answer}
            />
            {errors.answer && (
              <div className="text-danger small mt-1">{errors.answer}</div>
            )}
          </Form.Group>

          {/* Explanation */}
          <Form.Group className="mb-3">
            <Form.Label>Explanation (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              placeholder="Provide context or reasoning for the answer..."
            />
          </Form.Group>

          {/* Total Marks */}
          <Form.Group className="mb-3">
            <Form.Label>Total Marks <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="number"
              value={totalMarks}
              onChange={(e) => setTotalMarks(e.target.value)}
              min="1"
              step="0.5"
              isInvalid={!!errors.marks}
            />
            {errors.marks && (
              <div className="text-danger small mt-1">{errors.marks}</div>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={closeModal} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Saving...
            </>
          ) : editMode ? (
            "Update Question"
          ) : (
            "Save Question"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SubjectiveQueandAnsModal;